import { CheckIcon as CheckIconSolid } from '@heroicons/react/24/solid';
import { Chip, Link as HeroLink } from '@heroui/react';
import clsx from 'clsx';
import type React from 'react';

const colors = {
  red: 'bg-red-500/15 text-red-700 group-data-[hover]:bg-red-500/25 dark:bg-red-500/10 dark:text-red-400 dark:group-data-[hover]:bg-red-500/20',
  orange:
    'bg-orange-500/15 text-orange-700 group-data-[hover]:bg-orange-500/25 dark:bg-orange-500/10 dark:text-orange-400 dark:group-data-[hover]:bg-orange-500/20',
  amber:
    'bg-amber-400/20 text-amber-700 group-data-[hover]:bg-amber-400/30 dark:bg-amber-400/10 dark:text-amber-400 dark:group-data-[hover]:bg-amber-400/15',
  yellow:
    'bg-yellow-400/20 text-yellow-700 group-data-[hover]:bg-yellow-400/30 dark:bg-yellow-400/10 dark:text-yellow-300 dark:group-data-[hover]:bg-yellow-400/15',
  lime: 'bg-lime-400/20 text-lime-700 group-data-[hover]:bg-lime-400/30 dark:bg-lime-400/10 dark:text-lime-300 dark:group-data-[hover]:bg-lime-400/15',
  green:
    'bg-green-500/15 text-green-700 group-data-[hover]:bg-green-500/25 dark:bg-green-500/10 dark:text-green-400 dark:group-data-[hover]:bg-green-500/20',
  emerald:
    'bg-emerald-500/15 text-emerald-700 group-data-[hover]:bg-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400 dark:group-data-[hover]:bg-emerald-500/20',
  teal: 'bg-teal-500/15 text-teal-700 group-data-[hover]:bg-teal-500/25 dark:bg-teal-500/10 dark:text-teal-300 dark:group-data-[hover]:bg-teal-500/20',
  cyan: 'bg-cyan-400/20 text-cyan-700 group-data-[hover]:bg-cyan-400/30 dark:bg-cyan-400/10 dark:text-cyan-300 dark:group-data-[hover]:bg-cyan-400/15',
  sky: 'bg-sky-500/15 text-sky-700 group-data-[hover]:bg-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300 dark:group-data-[hover]:bg-sky-500/20',
  blue: 'bg-primary-500/15 text-primary-700 group-data-[hover]:bg-primary-500/25 dark:text-primary-400 dark:group-data-[hover]:bg-primary-500/25',
  indigo:
    'bg-secondary-500/15 text-secondary-700 group-data-[hover]:bg-secondary-500/25 dark:text-secondary-400 dark:group-data-[hover]:bg-secondary-500/20',
  violet:
    'bg-violet-500/15 text-violet-700 group-data-[hover]:bg-violet-500/25 dark:text-violet-400 dark:group-data-[hover]:bg-violet-500/20',
  purple:
    'bg-purple-500/15 text-purple-700 group-data-[hover]:bg-purple-500/25 dark:text-purple-400 dark:group-data-[hover]:bg-purple-500/20',
  fuchsia:
    'bg-fuchsia-400/15 text-fuchsia-700 group-data-[hover]:bg-fuchsia-400/25 dark:bg-fuchsia-400/10 dark:text-fuchsia-400 dark:group-data-[hover]:bg-fuchsia-400/20',
  pink: 'bg-pink-400/15 text-pink-700 group-data-[hover]:bg-pink-400/25 dark:bg-pink-400/10 dark:text-pink-400 dark:group-data-[hover]:bg-pink-400/20',
  rose: 'bg-rose-400/15 text-rose-700 group-data-[hover]:bg-rose-400/25 dark:bg-rose-400/10 dark:text-rose-400 dark:group-data-[hover]:bg-rose-400/20',
  zinc: 'bg-zinc-600/10 text-zinc-700 group-data-[hover]:bg-zinc-600/20 dark:bg-white/5 dark:text-zinc-400 dark:group-data-[hover]:bg-white/10',
  black:
    'bg-black text-white group-data-[hover]:bg-black/80 dark:bg-white/5 dark:text-black dark:group-data-[hover]:bg-white/10',
};

type BadgeProps = { color?: keyof typeof colors };

export function Badge({
  color = 'zinc',
  className,
  ...props
}: BadgeProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'inline-flex items-center gap-x-1.5 rounded-md px-1.5 py-0.5 text-sm/5 font-medium sm:text-xs/5 forced-colors:outline',
        colors[color],
      )}
    />
  );
}

// HeroUI-based BadgeButton
interface BadgeButtonProps {
  color?:
    | 'primary'
    | 'default'
    | 'success'
    | 'warning'
    | 'danger'
    | 'secondary'
    | 'emerald'
    | 'red'
    | 'amber'
    | 'blue'
    | 'zinc';
  onClick?: () => void;
  href?: string;
  children: React.ReactNode;
  className?: string;
  isChecked?: boolean;
}

const colorMap: Record<
  string,
  'primary' | 'default' | 'success' | 'warning' | 'danger' | 'secondary'
> = {
  emerald: 'success',
  red: 'danger',
  amber: 'warning',
  blue: 'primary',
  primary: 'primary',
  default: 'default',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  secondary: 'secondary',
  zinc: 'default',
};

export function BadgeButton({
  color = 'default',
  onClick,
  href,
  children,
  className,
  isChecked,
}: BadgeButtonProps) {
  const mappedColor = colorMap[color] || 'default';
  let variant = 'flat';
  if (isChecked !== undefined && isChecked) {
    variant = 'flat';
  } else if (isChecked !== undefined && !isChecked) {
    variant = 'light';
  }

  const chipContent = (
    <Chip
      size='sm'
      color={mappedColor}
      variant={variant as 'flat' | 'solid' | 'light' | 'bordered' | 'faded' | 'shadow' | 'dot'}
      onClick={!href ? onClick : undefined}
      classNames={{
        base: clsx(
          'cursor-pointer flex px-2 gap-1 flex-row flex-nowrap items-center transition-transform hover:scale-105',
          className,
        ),
        content: 'flex flex-nowrap flex-row gap-1',
      }}
      startContent={
        isChecked !== undefined ? (
          isChecked ? (
            <CheckIconSolid className='size-4' />
          ) : (
            <div className='size-3 rounded border border-current' />
          )
        ) : undefined
      }
    >
      {children}
    </Chip>
  );

  if (href) {
    return (
      <HeroLink href={href} onClick={onClick}>
        {chipContent}
      </HeroLink>
    );
  }

  return chipContent;
}
